import Axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import { ApiTokenStorage } from '@/module/auth/utils/ApiTokenStorage';

import { axiosConfig } from './axiosConfig';
import { getTraceId } from './getTraceId';

export const AXIOS_INSTANCE = Axios.create(axiosConfig);
AXIOS_INSTANCE.interceptors.response.use(
  res => {
    // CSV 파일 응답인 경우 에러 체크 건너뛰기
    const contentType = res.headers['content-type'];
    const isCsvResponse =
      typeof contentType === 'string' && contentType.includes('text/csv');

    if (res.status === 200 && !isCsvResponse && res.data.status !== 'SUCCESS') {
      throw ZepQuizApiError.fromResponse(
        res,
        res.data.message || '',
        res.data.status,
      );
    }

    return res;
  },
  (error: AxiosError<{ message?: string; data?: unknown; status: string }>) => {
    // 네트워크 에러 로깅 (Sentry에는 보고하지 않음)
    if (Axios.isAxiosError(error)) {
      if (!error.response && error.message === 'Network Error') {
        console.warn(
          '[Network Error] 네트워크 연결이 불안정하거나 서버에 접근할 수 없습니다.',
          {
            url: error.config?.url,
            method: error.config?.method,
          },
        );
      } else if (!error.response) {
        console.warn('[Network Error] 서버 응답 없음', {
          url: error.config?.url,
          method: error.config?.method,
          message: error.message,
        });
      } else if (error.code === 'ECONNABORTED') {
        console.warn('[Network Error] 요청이 타임아웃되었습니다.', {
          url: error.config?.url,
          method: error.config?.method,
          timeout: error.config?.timeout,
        });
      }
    }

    if (error.response?.data?.status) {
      throw ZepQuizApiError.fromResponse(
        error.response,
        error.response.data.message || 'Unknown error',
        error.response.data.status || 'Error',
      );
    }
    throw error;
  },
);

AXIOS_INSTANCE.interceptors.request.use(
  config => {
    config.headers['X-ZEP-Trace-Id'] = getTraceId();

    const apiToken = ApiTokenStorage.get();
    if (apiToken) {
      config.headers['Authorization'] = `Bearer ${apiToken}`;
    } else {
      delete config.headers['Authorization'];
    }

    // admin API의 경우 timeout을 3분으로 설정
    if (config.url && config.url.includes('/admin/')) {
      config.timeout = 3 * 60 * 1000; // 3분
    }

    if (config.params && config.params.request) {
      const requestParams = config.params.request;

      if (requestParams) {
        config.params = {
          ...config.params,
          ...requestParams,
        };
        delete config.params.request;
      }
      // 빈 문자열 값을 가진 키 삭제
      config.params = Object.fromEntries(
        Object.entries(config.params).filter(([, value]) => value !== ''),
      );
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

export class ZepQuizApiError<T = unknown> extends Error {
  public details: T;
  public status: string;
  public httpStatus: number;
  // Sentry beforeSend 가 요청 method·url·params·body 를 붙이는 데 쓴다.
  declare readonly config?: InternalAxiosRequestConfig;

  constructor(
    message: string,
    status: string,
    httpStatus: number,
    details?: T,
  ) {
    super(message);
    this.name = 'ZepQuizApiError';
    this.details = details || ({} as T);
    this.status = status;
    this.httpStatus = httpStatus;
    this.message = message;
    // 응답 원문은 Sentry console breadcrumb 에 가리지 않고 실리므로 찍지 않는다
    // (가린 본문은 contexts.axios.responseBody 에 있다).
    console.error('ZEP API ERROR :', status, httpStatus, message);
    Object.setPrototypeOf(this, ZepQuizApiError.prototype);
  }

  /** 응답 봉투로 에러를 만들고 원래 요청 config 를 붙인다. */
  static fromResponse<T>(
    response: AxiosResponse<T>,
    message: string,
    status: string,
  ): ZepQuizApiError<T> {
    const error = new ZepQuizApiError(
      message,
      status,
      response.status,
      response.data,
    );
    // 열거되지 않게 둬서 Authorization 헤더가 에러 직렬화에 섞이지 않게 한다.
    Object.defineProperty(error, 'config', {
      value: response.config,
      enumerable: false,
    });
    return error;
  }
}

// use your own URL here or environment variable

// add a second `options` argument here if you want to pass extra options to each generated query
export const httpsInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): CancellablePromise<T> => {
  const source = Axios.CancelToken.source();
  const promise = AXIOS_INSTANCE({
    ...config,
    ...options,
    cancelToken: source.token,
  }).then(({ data }) => data) as CancellablePromise<T>;

  promise.cancel = () => {
    source.cancel('Query was cancelled');
  };

  return promise;
};
interface CancellablePromise<T> extends Promise<T> {
  cancel: () => void;
}

export const enum HttpStatusCodeEnum {
  OK_200 = 200,
  CREATED_201 = 201,
  NO_CONTENT_204 = 204,
  BAD_REQUEST_400 = 400,
  UNAUTHORIZED_401 = 401,
  FORBIDDEN_403 = 403,
  NOT_FOUND_404 = 404,
  NOT_ACCEPTABLE_406 = 406,
  CONFLICT_409 = 409,
  PAYLOAD_TOO_LARGE_413 = 413,
  UNSUPPORTED_MEDIA_TYPE_415 = 415,
  UNPROCESSABLE_ENTITY_422 = 422,
  LOCKED_423 = 423,
  TOO_MANY_REQUESTS_429 = 429,
  INTERNAL_SERVER_ERROR_500 = 500,
  BAD_GATEWAY_502 = 502,
  SERVICE_UNAVAILABLE_503 = 503,
  GATEWAY_TIMEOUT_504 = 504,
}

// In some case with react-query and swr you want to be able to override the return error type so you can also do it here like this
export type ErrorType<Error> = ZepQuizApiError<Error>;

export type BodyType<BodyData> = BodyData;
